import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { CalendarClock, Play, CheckCircle2, Plus, Users as UsersIcon, X, AlertTriangle } from "lucide-react";

const VISIT_TYPES = ["Erect", "Alter", "Adapt", "Inspect", "Dismantle", "Callout", "Other"];

const STATUS_BADGE = {
  Planned: { bg: "#f1f5f9", fg: "#334155" },
  InProgress: { bg: "#fef3c7", fg: "#92400e" },
  Complete: { bg: "#dcfce7", fg: "#065f46" },
};

export default function Visits() {
  const { user } = useAuth();
  const canManage = ["admin", "contracts_manager", "supervisor"].includes(user?.role);

  const [visits, setVisits] = useState([]);
  const [structures, setStructures] = useState([]);
  const [operatives, setOperatives] = useState([]);
  const [filter, setFilter] = useState({ status: "", type: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.type) params.type = filter.type;
    const [v, s, o] = await Promise.all([
      api.get("/visits", { params }),
      api.get("/structures"),
      api.get("/operatives"),
    ]);
    setVisits(v.data);
    setStructures(s.data);
    setOperatives(o.data.filter((x) => x.status === "active"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.type]);

  const startVisit = async (v) => {
    setError(""); setSuccess("");
    try {
      await api.post(`/visits/${v.id}/start`);
      setSuccess(`Visit started: ${v.type} on ${v.structure_description || v.structure_id}`);
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const completeVisit = async (v) => {
    setError(""); setSuccess("");
    try {
      await api.post(`/visits/${v.id}/complete`);
      setSuccess(`Visit completed: ${v.type}`);
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="visits-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 20, display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>VISITS · GANGS · TIME ATTACHMENT POINT</div>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>Visits</h1>
          <div style={{ color: "#64748b", marginTop: 4 }}>A visit is a discrete job against a structure — Erect, Alter, Dismantle, Callout, etc. Time, gangs, supervisors and RAMS attach here, not to the structure.</div>
        </div>
        {canManage ? (
          <button data-testid="new-visit-btn" className="btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={16} style={{ marginRight: 6, verticalAlign: "middle" }} /> New visit
          </button>
        ) : null}
      </header>

      {error ? <div data-testid="visits-error" className="card" style={{ padding: 12, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", display: "flex", gap: 8, alignItems: "center" }}><AlertTriangle size={16} /> {error}</div> : null}
      {success ? <div data-testid="visits-success" className="card" style={{ padding: 12, marginBottom: 12, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", display: "flex", gap: 8, alignItems: "center" }}><CheckCircle2 size={16} /> {success}</div> : null}

      <div className="card" style={{ padding: 12, marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <label className="mono" style={{ fontSize: 12, color: "#64748b" }}>
          STATUS
          <select data-testid="filter-status" value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} style={{ marginLeft: 8 }}>
            <option value="">All</option>
            <option value="Planned">Planned</option>
            <option value="InProgress">In progress</option>
            <option value="Complete">Complete</option>
          </select>
        </label>
        <label className="mono" style={{ fontSize: 12, color: "#64748b" }}>
          TYPE
          <select data-testid="filter-type" value={filter.type} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))} style={{ marginLeft: 8 }}>
            <option value="">All</option>
            {VISIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="visits-table">
          <thead>
            <tr>
              <th>Structure</th>
              <th>Type</th>
              <th>Supervisor</th>
              <th>Gang</th>
              <th>Status</th>
              <th>Linked RAMS / Handover</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visits.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No visits match the current filter.</td></tr>
            ) : visits.map((v) => {
              const badge = STATUS_BADGE[v.status] || { bg: "#f1f5f9", fg: "#334155" };
              return (
                <tr key={v.id} data-testid={`visit-row-${v.id}`}>
                  <td data-label="Structure"><Link to={`/structures/${v.structure_id}`} style={{ color: "#0f172a", fontWeight: 600 }}>{v.structure_description || "—"}</Link></td>
                  <td data-label="Type"><span className="mono" style={{ background: "#0f172a", color: "white", padding: "2px 8px", borderRadius: 3, fontSize: 11 }}>{v.type}</span></td>
                  <td data-label="Supervisor">{v.supervisor_name || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                  <td data-label="Gang">
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <UsersIcon size={14} color="#64748b" />
                      <span data-testid={`visit-gang-count-${v.id}`}>{(v.gang || []).length}</span>
                      {(v.gang || []).length > 0 ? (
                        <span style={{ color: "#64748b", fontSize: 12 }}>· {(v.gang || []).slice(0, 3).map((g) => g.operative_name).join(", ")}{(v.gang || []).length > 3 ? "…" : ""}</span>
                      ) : null}
                    </div>
                  </td>
                  <td data-label="Status"><span className="mono" style={{ background: badge.bg, color: badge.fg, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700 }}>{v.status}</span></td>
                  <td data-label="Linked" style={{ fontSize: 12, color: "#64748b" }}>
                    {v.rams_id ? <span data-testid={`visit-rams-linked-${v.id}`}>RAMS ✓</span> : <span style={{ color: "#cbd5e1" }}>no RAMS</span>}
                    {" · "}
                    {v.handover_id ? <span data-testid={`visit-handover-linked-${v.id}`}>Handover ✓</span> : <span style={{ color: "#cbd5e1" }}>no handover</span>}
                  </td>
                  <td data-label="Actions" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {canManage && v.status === "Planned" ? (
                      <button data-testid={`start-visit-${v.id}`} className="btn-ghost" onClick={() => startVisit(v)}><Play size={12} style={{ marginRight: 4 }} />Start</button>
                    ) : null}
                    {canManage && v.status === "InProgress" ? (
                      <button data-testid={`complete-visit-${v.id}`} className="btn-primary" style={{ marginLeft: 6 }} onClick={() => completeVisit(v)}><CheckCircle2 size={12} style={{ marginRight: 4 }} />Complete</button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew ? (
        <NewVisitModal
          structures={structures}
          operatives={operatives}
          onClose={() => setShowNew(false)}
          onCreated={(v) => { setShowNew(false); setSuccess(`Visit created: ${v.type}`); load(); }}
          onError={(msg) => setError(msg)}
        />
      ) : null}
    </div>
  );
}

function NewVisitModal({ structures, operatives, onClose, onCreated, onError }) {
  const [form, setForm] = useState({
    structure_id: "", type: "Erect", supervisor_id: "", planned_start: "", notes: "",
    out_of_hours: false, callout_received_at: "", on_site_at: "", callout_description: "",
  });
  const [gangIds, setGangIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const supervisors = useMemo(() => operatives.filter((o) =>
    (o.role || "").toLowerCase().includes("supervisor") || (o.role || "").toLowerCase().includes("twc") || o.rams_signoff_authority
  ), [operatives]);

  const toggleGang = (op) => {
    setGangIds((ids) => ids.includes(op.id) ? ids.filter((x) => x !== op.id) : [...ids, op.id]);
  };

  // Auto-flag out_of_hours when type=Callout, and vice versa keep them sensible
  const setType = (t) => {
    setForm((f) => ({ ...f, type: t, out_of_hours: t === "Callout" ? true : f.out_of_hours }));
  };

  const submit = async () => {
    if (!form.structure_id) { onError("Pick a structure"); return; }
    const supervisor = operatives.find((o) => o.id === form.supervisor_id);
    setBusy(true);
    try {
      const payload = {
        structure_id: form.structure_id,
        type: form.type,
        supervisor_id: form.supervisor_id || null,
        supervisor_name: supervisor ? supervisor.full_name : "",
        gang: gangIds.map((id) => {
          const op = operatives.find((o) => o.id === id);
          return { operative_id: id, operative_name: op?.full_name || "" };
        }),
        planned_start: form.planned_start || null,
        notes: form.notes,
        out_of_hours: form.out_of_hours,
        callout_received_at: form.out_of_hours ? (form.callout_received_at || null) : null,
        on_site_at: form.out_of_hours ? (form.on_site_at || null) : null,
        callout_description: form.callout_description || "",
      };
      const { data } = await api.post("/visits", payload);
      onCreated(data);
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 }}>
      <div data-testid="new-visit-modal" className="card" style={{ background: "white", maxWidth: 720, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontWeight: 900 }}>New visit</h2>
          <button data-testid="close-new-visit" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Structure *">
            <select data-testid="new-visit-structure" value={form.structure_id} onChange={(e) => setForm({ ...form, structure_id: e.target.value })}>
              <option value="">— pick a structure —</option>
              {structures.map((s) => <option key={s.id} value={s.id}>{s.description?.slice(0, 80) || s.id}</option>)}
            </select>
          </Field>
          <Field label="Type">
            <select data-testid="new-visit-type" value={form.type} onChange={(e) => setType(e.target.value)}>
              {VISIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Supervisor">
            <select data-testid="new-visit-supervisor" value={form.supervisor_id} onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })}>
              <option value="">— none —</option>
              {supervisors.map((o) => <option key={o.id} value={o.id}>{o.full_name} · {o.role}</option>)}
            </select>
          </Field>
          <Field label="Planned start">
            <input data-testid="new-visit-planned" type="date" value={form.planned_start} onChange={(e) => setForm({ ...form, planned_start: e.target.value })} />
          </Field>
        </div>

        <div style={{ marginTop: 16, padding: 12, background: form.out_of_hours ? "#fef3c7" : "#f8fafc", border: `1px solid ${form.out_of_hours ? "#f59e0b" : "#e2e8f0"}`, borderRadius: 4 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input data-testid="new-visit-out-of-hours" type="checkbox" checked={form.out_of_hours} onChange={(e) => setForm({ ...form, out_of_hours: e.target.checked })} />
            <span style={{ fontWeight: 700 }}>Out-of-hours / Callout</span>
            <span className="mono" style={{ fontSize: 11, color: "#64748b" }}>(adds 7A charge · drives the 60-min KPI)</span>
          </label>
          {form.out_of_hours ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              <Field label="Callout received at *">
                <input data-testid="new-visit-received-at" type="datetime-local" value={form.callout_received_at} onChange={(e) => setForm({ ...form, callout_received_at: e.target.value })} />
              </Field>
              <Field label="On-site at">
                <input data-testid="new-visit-onsite-at" type="datetime-local" value={form.on_site_at} onChange={(e) => setForm({ ...form, on_site_at: e.target.value })} />
              </Field>
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="What was the callout for?">
                  <input data-testid="new-visit-callout-desc" type="text" placeholder="e.g. silo bay loose tie reported by night-shift" value={form.callout_description} onChange={(e) => setForm({ ...form, callout_description: e.target.value })} />
                </Field>
              </div>
            </div>
          ) : null}
        </div>

        <div style={{ marginTop: 16 }}>
          <div className="mono" style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>GANG · {gangIds.length} selected</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 6, maxHeight: 200, overflowY: "auto", border: "1px solid #e2e8f0", padding: 8, borderRadius: 4 }}>
            {operatives.map((o) => (
              <label key={o.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 8px", background: gangIds.includes(o.id) ? "#fef3c7" : "white", border: "1px solid #e2e8f0", borderRadius: 3, cursor: "pointer" }}>
                <input data-testid={`gang-op-${o.id}`} type="checkbox" checked={gangIds.includes(o.id)} onChange={() => toggleGang(o)} />
                <span style={{ fontSize: 13 }}>{o.full_name}</span>
                <span className="mono" style={{ fontSize: 10, color: "#64748b" }}>· {o.role}</span>
              </label>
            ))}
          </div>
        </div>

        <Field label="Notes">
          <textarea data-testid="new-visit-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button data-testid="submit-new-visit" className="btn-primary" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create visit"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
